import { useEffect, useState } from "react";
import { Copy, Loader2, Mail, RotateCw, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";

const ROLES = [["pastor", "Pastor / Leadership"], ["finance", "Finance"], ["event_admin", "Event Admin"],
  ["community_leader", "Community Leader"], ["ministry_leader", "Ministry Leader"],
  ["content_admin", "Content Admin"], ["super_admin", "Super Admin"]];

const STATUS = {
  pending: ["Menunggu", "bg-amber-100 text-amber-700"],
  accepted: ["Diterima", "bg-emerald-100 text-emerald-700"],
};

export default function Invitations() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "content_admin" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/invitations").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/invitations", form);
      toast.success(`Undangan terkirim ke ${form.email}`);
      setForm({ email: "", name: "", role: form.role });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (inv) => {
    try {
      await api.post(`/admin/invitations/${inv.id}/resend`);
      toast.success(`Undangan dikirim ulang ke ${inv.email}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Batalkan undangan untuk ${inv.email}?`)) return;
    try {
      await api.delete(`/admin/invitations/${inv.id}`);
      setList((l) => l.filter((x) => x.id !== inv.id));
      toast.success("Undangan dibatalkan");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success("Tautan undangan disalin");
  };

  return (
    <div data-testid="admin-invitations">
      <AdminTitle eyebrow="Akses & Peran" title="Undangan Pengurus" />
      <div className="grid lg:grid-cols-[22rem_1fr] gap-6">
        <form onSubmit={send} className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 h-fit" data-testid="invite-create-form">
          <p className="font-extrabold text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Kirim Undangan Baru</p>
          <p className="text-xs text-slate-500 mt-1">Pengurus baru akan menerima email berisi tautan untuk mengatur password dan langsung masuk OPC dengan peran yang Anda tentukan.</p>
          <div className="mt-5 space-y-3">
            <label className="block">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email pengurus</span>
              <input type="email" required className={`${inputCls} mt-1.5`} placeholder="nama@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nama</span>
              <input required minLength={2} className={`${inputCls} mt-1.5`} placeholder="Nama lengkap" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-field" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Peran / Role</span>
              <select className={`${inputCls} mt-1.5`} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role-select">
                {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="invite-send-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Kirim Undangan</>}</button>
          </div>
        </form>

        {!list ? <Skeleton className="h-80" /> : (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden" data-testid="invitations-list">
            {list.length === 0 ? <p className="p-10 text-center text-slate-500">Belum ada undangan. Kirim undangan pertama Anda.</p> : (
              <div className="divide-y divide-slate-100">
                {list.map((inv) => {
                  const [label, cls] = STATUS[inv.status] || ["—", "bg-slate-100 text-slate-600"];
                  return (
                    <div key={inv.id} className="flex flex-wrap items-center gap-3 p-5" data-testid={`invitation-row-${inv.id}`}>
                      <div className="flex-1 min-w-52">
                        <p className="font-bold text-slate-900">{inv.name} <span className={`ml-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${cls}`} data-testid={`invitation-status-${inv.id}`}>{label}</span></p>
                        <p className="text-xs text-slate-500">{inv.email} · <span className="font-semibold text-blue-600">{inv.role_label}</span></p>
                        <p className="text-[11px] text-slate-400 mt-0.5">Dikirim {fmtDate(inv.created_at, { day: "numeric", month: "short" })} oleh {inv.invited_by_name}{inv.status === "accepted" && inv.accepted_at ? ` · diterima ${fmtDate(inv.accepted_at, { day: "numeric", month: "short" })}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {inv.status === "pending" && (
                          <>
                            <button onClick={() => copyLink(inv.invite_url)} title="Salin tautan" data-testid={`invitation-copy-${inv.id}`} className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 grid place-items-center hover:bg-slate-200 transition"><Copy className="w-4 h-4" /></button>
                            <button onClick={() => resend(inv)} title="Kirim ulang" data-testid={`invitation-resend-${inv.id}`} className="w-9 h-9 rounded-xl bg-slate-100 text-blue-600 grid place-items-center hover:bg-blue-50 transition"><RotateCw className="w-4 h-4" /></button>
                          </>
                        )}
                        <button onClick={() => revoke(inv)} title="Hapus undangan" data-testid={`invitation-delete-${inv.id}`} className="w-9 h-9 rounded-xl bg-slate-100 text-red-600 grid place-items-center hover:bg-red-50 transition"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
